import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"
import _imports_0 from '@/assets/close-icon-2.svg'


const _hoisted_1 = {
  key: 0,
  class: "fixed overflow-y-auto inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50"
}
const _hoisted_2 = { class: "absolute overflow-y-auto top-5 w-[630px] p-6 mx-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg shadow-lg" }
const _hoisted_3 = { class: "flex justify-between border-b border-gray-300 pb-3" }
const _hoisted_4 = { class: "flex flex-col w-11/12" }
const _hoisted_5 = { class: "text-xl leading-[32px] tracking-[0.01rem] text-primary-text-color font-semibold" }
const _hoisted_6 = { class: "text-base leading-[26px] tracking-[0.01rem] text-[#898790] font-medium pt-4" }
const _hoisted_7 = {
  key: 0,
  class: "text-sm text-red-400 font-medium mt-1"
}
const _hoisted_8 = {
  key: 0,
  class: "mt-4 p-3 bg-[#F9F8F6] border border-[#E3E1EA] rounded-[5px] flex items-center justify-between"
}
const _hoisted_9 = { class: "flex items-center gap-2 overflow-hidden" }
const _hoisted_10 = { class: "text-sm text-primary-text-color font-normal tracking-[0.01rem] truncate" }
const _hoisted_11 = { class: "flex flex-col gap-2 mt-[36px]" }
const _hoisted_12 = {
  for: "document-file",
  class: "px-4 py-2 text-[#393840] font-medium rounded-md cursor-pointer transition flex items-center gap-1 border-2 border-[#393840] w-fit hover:bg-[#F9F8F6]"
}
const _hoisted_13 = {
  key: 0,
  class: "text-sm text-red-400 font-medium mt-1"
}
const _hoisted_14 = { class: "flex justify-end" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = {
  key: 0,
  class: "w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"
}
const _hoisted_17 = { key: 1 }

import client from "@/utils/axios";
import SelectMenu from "./SelectMenu.vue";
import { ref, reactive, computed } from "vue";
import { errorToast, successToast } from "@/utils/custom-toast";
import { useTranslate } from "@/composables/useTranslate";
import { useUserStore } from "@/stores/user";

type Props = {
  isOpen: boolean;
  projectId: number | string;
};

type Emits = {
  (event: "toggleModal"): void;
  (event: "refresh"): void;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadDocumentModal',
  props: {
    isOpen: { type: Boolean },
    projectId: {}
  },
  emits: ["toggleModal", "refresh"],
  setup(__props: any, { emit: __emit }) {

const DOCUMENT_CATEGORIES = [
  { id: 1, key: "phase_report", en: "Phase Report", de: "Phasenbericht" },
  {
    id: 2,
    key: "abnahme_protokoll",
    en: "Acceptance Protocol",
    de: "Abnahmeprotokoll",
  },
  { id: 3, key: "pruefbericht", en: "Inspection Report", de: "Prüfbericht" },
  { id: 4, key: "baumappe", en: "Construction Folder", de: "Baumappe" },
  { id: 5, key: "ac_report", en: "AC Report", de: "AC-Bericht" },
  { id: 6, key: "dc_report", en: "DC Report", de: "DC-Bericht" },
  { id: 7, key: "vob_report", en: "VOB Report", de: "VOB-Bericht" },
  {
    id: 8,
    key: "quality_guideline",
    en: "Quality Guideline",
    de: "Qualitätsrichtlinie",
  },
  {
    id: 9,
    key: "gefahrdungsbeurteilung",
    en: "Risk Assessment",
    de: "Gefährdungsbeurteilung",
  },
  { id: 10, key: "material_list", en: "Material List", de: "Materialliste" },
  {
    id: 11,
    key: "abnahmebericht",
    en: "Acceptance Report",
    de: "Abnahmebericht",
  },
  {
    id: 12,
    key: "funf_sicherheitsregeln",
    en: "Five Safety Rules",
    de: "Fünf Sicherheitsregeln",
  },
];

const props = __props;

const emits = __emit;
const { t } = useTranslate();
const userStore = useUserStore();

const categoryOptions = computed(() => {
  const lang = userStore.language === "de" ? "de" : "en";
  return DOCUMENT_CATEGORIES.map((cat) => ({
    id: cat.id,
    name: cat[lang],
  }));
});

const selectedDocumentType = ref<{ id: number; name: string } | null>(null);
const file = ref<File | null>(null);
const fileName = ref("");
const isLoading = ref(false);
const fileInputRef = ref<HTMLInputElement | null>(null);

const errors = reactive({
  documentType: "",
  file: "",
});

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const selectedFile = target.files?.[0];
  if (selectedFile) {
    file.value = selectedFile;
    fileName.value = selectedFile.name;
    errors.file = "";
  }
};

const removeFile = () => {
  file.value = null;
  fileName.value = "";
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const validate = () => {
  let isValid = [];
  errors.documentType = "";
  errors.file = "";

  if (!selectedDocumentType.value) {
    errors.documentType = "Please select a document category!";
    isValid.push(false);
  } else {
    isValid.push(true);
    errors.documentType = "";
  }

  if (!file.value) {
    errors.file = "Please select a file!";
    isValid.push(false);
  } else {
    isValid.push(true);
    errors.file = "";
  }

  return isValid.filter((item: boolean) => !item).length === 0;
};

const resetForm = () => {
  selectedDocumentType.value = null;
  file.value = null;
  fileName.value = "";
  errors.documentType = "";
  errors.file = "";
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const onSubmit = async () => {
  if (!validate() || isLoading.value) return;

  isLoading.value = true;

  const formData = new FormData();
  formData.append("file", file.value as File);
  formData.append("documentTypeId", String(selectedDocumentType.value?.id));

  try {
    await client.post(
      `/projects/${props.projectId}/upload-document`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    isLoading.value = false;
    successToast("Document uploaded successfully.");
    resetForm();
    emits("refresh");
  } catch (error: unknown) {
    isLoading.value = false;
    const err = error as { response?: { data?: { message?: string } } };
    if (err.response?.data?.message) {
      errorToast(err.response.data.message);
      return;
    }
    errorToast("Failed to upload document.");
  }
};

return (_ctx: any,_cache: any) => {
  return (__props.isOpen)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("h4", _hoisted_5, _toDisplayString(_unref(t)("projects.upload_document")), 1)
            ]),
            _createElementVNode("button", {
              class: "w-1/12 h-4 mt-2 flex justify-center",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (emits('toggleModal')))
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("img", {
                src: _imports_0,
                alt: "Close icon"
              }, null, -1)
            ]))])
          ]),
          _createElementVNode("form", {
            onSubmit: _withModifiers(onSubmit, ["prevent"])
          }, [
            _createElementVNode("div", null, [
              _createElementVNode("h4", _hoisted_6, _toDisplayString(_unref(t)("projects.document_category")), 1),
              _createElementVNode("div", null, [
                _createVNode(SelectMenu, {
                  modelValue: selectedDocumentType.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedDocumentType).value = $event)),
                  "is-error": !!errors.documentType,
                  options: categoryOptions.value
                }, null, 8, ["modelValue", "is-error", "options"]),
                (errors.documentType)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(errors.documentType), 1))
                  : _createCommentVNode("", true)
              ])
            ]),
            (fileName.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _cache[3] || (_cache[3] = _createElementVNode("svg", {
                      width: "16",
                      height: "16",
                      viewBox: "0 0 16 16",
                      fill: "none",
                      xmlns: "http://www.w3.org/2000/svg",
                      class: "shrink-0"
                    }, [
                      _createElementVNode("path", {
                        d: "M9.33333 1.33334H4.00001C3.64638 1.33334 3.30724 1.47381 3.05719 1.72386C2.80714 1.97391 2.66667 2.31305 2.66667 2.66667V13.3333C2.66667 13.687 2.80714 14.0261 3.05719 14.2762C3.30724 14.5262 3.64638 14.6667 4.00001 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2762C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33334L9.33333 1.33334Z",
                        stroke: "#898790",
                        "stroke-width": "1.5",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round"
                      }),
                      _createElementVNode("path", {
                        d: "M9.33333 1.33334V5.33334H13.3333",
                        stroke: "#898790",
                        "stroke-width": "1.5",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round"
                      })
                    ], -1)),
                    _createElementVNode("span", _hoisted_10, _toDisplayString(fileName.value), 1)
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    onClick: removeFile,
                    class: "shrink-0 ml-2 text-[#898790] hover:text-primary-text-color duration-200"
                  }, [...(_cache[4] || (_cache[4] = [
                    _createElementVNode("svg", {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 14 14",
                      fill: "none",
                      xmlns: "http://www.w3.org/2000/svg"
                    }, [
                      _createElementVNode("path", {
                        d: "M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5",
                        stroke: "currentColor",
                        "stroke-width": "1.5",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round"
                      })
                    ], -1)
                  ]))])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("label", _hoisted_12, [
                _cache[5] || (_cache[5] = _createElementVNode("svg", {
                  width: "15",
                  height: "16",
                  viewBox: "0 0 15 16",
                  fill: "none",
                  xmlns: "http://www.w3.org/2000/svg"
                }, [
                  _createElementVNode("path", {
                    d: "M1.40186 7.59852L6.57257 2.4278C8.11026 0.890114 10.6034 0.890114 12.1411 2.4278C13.6788 3.96549 13.6786 6.45872 12.1409 7.99641L6.17468 13.9626C5.14956 14.9877 3.48778 14.9876 2.46265 13.9625C1.43753 12.9373 1.43728 11.2755 2.4624 10.2503L8.42862 4.28413C8.94118 3.77156 9.77265 3.77156 10.2852 4.28413C10.7978 4.79669 10.7974 5.62752 10.2849 6.14008L5.11414 11.3108",
                    stroke: "#393840",
                    "stroke-width": "2",
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round"
                  })
                ], -1)),
                _createTextVNode(" " + _toDisplayString(_unref(t)("global.attach_files")), 1)
              ]),
              _createElementVNode("input", {
                ref_key: "fileInputRef",
                ref: fileInputRef,
                class: "hidden",
                type: "file",
                id: "document-file",
                accept: ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg",
                onChange: handleFileChange
              }, null, 544),
              (errors.file)
                ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(errors.file), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("button", {
                type: "submit",
                disabled: isLoading.value,
                class: "mt-5 flex items-center justify-center py-3 px-[56px] text-base text-white bg-[#393840] tracking-[0.01rem] font-semibold outline-none rounded-primary-radius border-[2px] border-[#393840] cursor-pointer hover:bg-[#28272c] duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              }, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16))
                  : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_unref(t)("global.save")), 1))
              ], 8, _hoisted_15)
            ])
          ], 32)
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})